import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@wds/box';
import { Typography } from '@wds/typography';
import { Input } from '@wds/input';
import { Button } from '@wds/button';
import { Slide } from '@wds/motion';
import { ProgressCircle } from '@wds/progress-circle';

import { getCookie } from 'sma.common/utils';
import PropTypes from 'prop-types';

import STRINGS from '../constants';
import MFAErrorMessage from './MFAErrorMessage';

const EmailEnrollment = (props) => {
	const {
		isLoaded,
		context,
		setContext,
		error,
		setError,
		isSliding,
		setIsSliding,
		handleChangeMethod,
		handleError,
		currentPage,
		setCurrentPage,
		isChange,
		cooldown,
		startCooldown,
		isModal,
		handleOnFinish,
	} = props;

	const [isValid, setIsValid] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [message, setMessage] = useState('');
	const [started, setStarted] = useState(
		context?.status === 'code_pending' || context?.status === 'code_already_pending'
	);
	const handledStatusKeyRef = useRef('');
	const isMountedRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	useEffect(() => {
		setMessage('');
	}, [currentPage]);

	useEffect(() => {
		if (currentPage !== 'email-enrollment') {
			handledStatusKeyRef.current = '';
			return;
		}

		const status = context?.status;
		if (!status) {
			return;
		}

		const remaining = Number(context?.cooldown_remaining) || 0;
		const statusKey = `${status}:${remaining}`;
		if (handledStatusKeyRef.current === statusKey) {
			return;
		}

		if (status === 'code_already_pending') {
			setStarted(true);
			setMessage(remaining > 0 ? STRINGS.code_already_pending : '');
			setError('');
			if (remaining > 0) {
				startCooldown(remaining);
			}
		} else if (status === 'code_pending') {
			setStarted(true);
			setMessage(STRINGS.email_sent);
			setError('');
			if (remaining > 0) {
				startCooldown(remaining);
			} else if (cooldown === 0) {
				startCooldown();
			}
		}

		handledStatusKeyRef.current = statusKey;
	}, [currentPage, context?.status, context?.cooldown_remaining, cooldown, startCooldown]);

	useEffect(() => {
		if (currentPage !== 'email-enrollment') return;
		if (context?.status === 'code_already_pending' && cooldown === 0) {
			setMessage('');
		}
	}, [currentPage, context?.status, cooldown]);

	useEffect(() => {
		if (context.challengeCode.match(/^[0-9]+$/) && context.challengeCode.length === 6) {
			setIsValid(true);
		} else {
			setIsValid(false);
		}
	}, [context.challengeCode]);

	const onHandleContextChange = (key) => (e) => {
		let { value } = e.target;
		if (key === 'challengeCode') {
			value = value.replace(/\D/g, '').slice(0, 6);
		}
		setContext({ ...context, [key]: value });
	};

	const startEmailEnrollment = async () => {
		setIsLoading(true);
		setMessage('');
		try {
			let url;
			let action;
			let method;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'begin_device_change';
				method = 'email';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'setup_email';
				method = 'email';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action, method }),
			});
			const data = await response.json();		if (!isMountedRef.current) return;			if (data.success) {
				setError('');
				setMessage(data.message);
				setStarted(true);
				startCooldown();
				// update method_id from response if available
				if(data.method?.id) {
					setContext(prev => ({
						...prev,
						method_id: data.method.id,
						status: 'code_pending',
					}));
				}
				setIsLoading(false);
			} else {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
					return;
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_already_pending',
						cooldown_remaining: data.time_remaining || 0,
					});
					startCooldown(data.time_remaining);
				} else if (data.error) {
					setError(data.error);
				}
			}
		} catch (err) {
			handleError(`Error starting email enrollment: ${err}`);
		} finally {
			if (isMountedRef.current) {
				setIsLoading(false);
			}
		}
	};

	const resendEmailCode = async () => {
		setIsLoading(true);
		setMessage('');
		try {
			const action = 'send_code';
			const method = 'email';
			let url;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
			} else {
				url = reverse('api-internal-mfa-enroll');
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({ action, method }),
			});
			const data = await response.json();		if (!isMountedRef.current) return;			if (data.success) {
				setError('');
				setMessage(data.message);
				setStarted(true);
				startCooldown();
			} else {
				if (data.error === 'invalid_code_locked_out' || data.error === 'not_authenticated') {
					window.location.href = reverse('mfa-lockout');
					return;
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_already_pending',
						cooldown_remaining: data.time_remaining || 0,
					});
					startCooldown(data.time_remaining);
				} else if (data.error) {
					setError(data.error);
				}
			}
		} catch (err) {
			handleError(`Error resending email code: ${err}`);
		} finally {
			if (isMountedRef.current) {
				setIsLoading(false);
			}
		}
	};

	const submitChallengeCode = async () => {
		setIsLoading(true);
		try {
			let url;
			let action;
			if (isChange) {
				url = reverse('api-internal-mfa-change-device');
				action = 'complete_device_change';
			} else {
				url = reverse('api-internal-mfa-enroll');
				action = 'verify_email_enrollment';
			}
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-CSRFToken': getCookie('csrftoken'),
				},
				body: JSON.stringify({
					action,
					method_id: context.method_id,
					challenge_code: context.challengeCode,
					remember_device: context.rememberDevice,
					method: 'email',
				}),
			});
			const data = await response.json();
			if (!isMountedRef.current) return;
			if (data.error) {
				if (data.error === 'not_authenticated' || data.error === 'invalid_code_locked_out') {
					window.location.href = reverse('mfa-lockout');
					return;
				} else if (data.error === 'code_already_pending') {
					setContext({
						...context,
						challengeCode: '',
						status: 'code_already_pending',
						cooldown_remaining: data.time_remaining || 0,
					});
					startCooldown(data.time_remaining);
				} else {
					setError(data.error);
				}
			}
			// Check if recovery email is needed
			if (data.needs_recovery_email) {
				setContext((prev) => ({
					...prev,
					mfa_enabled: true,
					mfa_methods: [{ method: 1 }],
					needsRecoveryEmail: true,
				}));
				setCurrentPage('recovery-email-setup');
				return;
			} else if (data.enrollment_complete) {
				// User already has recovery email - enrollment complete.
				// In account settings modal, close modal instead of redirecting away.
				if (isModal) {
					handleOnFinish();
				} else {
					window.location.replace('/');
				}
				return;
			} else if (data.recovery_codes || (isChange && data.success)) {
				setContext((prev) => ({
					...prev,
					recovery_codes: data.recovery_codes || prev.recovery_codes,
					mfa_enabled: true,
					mfa_methods: data.mfa_methods || [{ method: 1 }], // email
				}));
				setCurrentPage('enrollment-complete');
			}
		} catch (err) {
			handleError(`Error submitting challenge code: ${err}`);
		} finally {
			if (isMountedRef.current) {
				setIsLoading(false);
			}
		}
	};

	const inputColor = error ? 'error' : '';

	return (
		<Slide
			in={isLoaded && currentPage === 'email-enrollment'}
			speed={currentPage === 'email-enrollment' ? 'slow' : undefined}
			timeout={currentPage === 'email-enrollment' ? undefined : 150}
			direction='right'
			onEnter={() => setIsSliding(true)}
			onExited={() => setIsSliding(false)}
			alwaysRenderContent
		>
			<Box
				display='flex'
				flexDirection='column'
				gap={4}
				style={{ position: isSliding ? 'absolute' : 'static', width: '100%' }}
			>
				<Box
					display='flex'
					flexDirection='column'
					justifyContent='center'
					alignItems='center'
					gap={4}
				>
					<Box
						display='flex'
						flexDirection='column'
						justifyContent='center'
						alignItems='center'
						gap={4}
					>
						<Typography variant='sectionTitleSm' align='center'>
							{STRINGS.setUpMFA}
						</Typography>
						<MFAErrorMessage error={error} />
						{isLoading && (
							<Box mb={5} role='status' aria-live='polite'>
								<ProgressCircle continuous />
							</Box>
						)}
						{message && (
							<Box
								p={4}
								style={{
									backgroundColor: '#42ac14',
									borderRadius: '5px',
									color: '#FFFFFF',
								}}
								role='alert'
								aria-live='assertive'
							>
								{STRINGS?.[message] || message}
							</Box>
						)}
						<Typography variant='bodySm' align='center' weight='medium'>
							{STRINGS.emailMFAWarning}
						</Typography>
						{started || cooldown ? (
							<Button
								onClick={() => resendEmailCode()}
								id='start-resend-email-button'
								size='lg'
								aria-label={STRINGS.resendCode}
								disabled={isLoading || cooldown > 0}
							>
								{cooldown > 0 ? `${STRINGS.resendCode} (${cooldown}s)` : (STRINGS.resendCode)}
							</Button>
						) : (
						<Button
							onClick={() => startEmailEnrollment()}
							id='start-email-enrollment-button'
							size='lg'
							aria-label={STRINGS.startEmailEnrollment}
							disabled={isLoading || cooldown > 0}
						>
							{!isLoading ? (
								STRINGS.startEmailEnrollment
							) : (
								<ProgressCircle size='sm' continuous />
							)}
						</Button>
						)}
					</Box>
				</Box>
				<Box display='flex' alignItems='center' width='100%'>
					<hr
						style={{
							flex: 1,
							border: 'none',
							borderTop: '1px solid #cccccc80',
							margin: 0,
						}}
					/>
					<Box px={2}>
						<Typography
							variant='caption'
							style={{ textTransform: 'uppercase' }}
						>
							{STRINGS.then}
						</Typography>
					</Box>
					<hr
						style={{
							flex: 1,
							border: 'none',
							borderTop: '1px solid #cccccc80',
							margin: 0,
						}}
					/>
				</Box>
				<Box
					display='flex'
					flexDirection='column'
					gap={4}
					pb={6}
				>
				<Input
					value={context.challengeCode}
					placeholder={STRINGS.enterCodeProvided}
					onChange={onHandleContextChange('challengeCode')}
					id='mfa-challenge-code'
					type='text'
					inputMode='numeric'
					maxLength={6}
					color={inputColor}
					stretched
					aria-required='true'
					autoFocus
				/>
				<Button
					id='next-button'
					size='lg'
					disabled={isLoading || !isValid}
					aria-disabled={!isValid || isLoading}
					onClick={submitChallengeCode}
				>
					{!isLoading ? (
						STRINGS.continue
					) : (
						<ProgressCircle size='sm' continuous />
					)}
				</Button>
					<Typography
						variant='link'
						component='a'
						href='#'
						align='center'
						onClick={(e) => {
							e.preventDefault();
							handleChangeMethod();
						}}
					>
						{STRINGS.setupUsingAuthenticatorApp}
					</Typography>
				</Box>
			</Box>
		</Slide>
	);
};

EmailEnrollment.propTypes = {
	isLoaded: PropTypes.bool.isRequired,
	context: PropTypes.object.isRequired,
	setContext: PropTypes.func.isRequired,
	error: PropTypes.string,
	isSliding: PropTypes.bool.isRequired,
	setIsSliding: PropTypes.func.isRequired,
	handleChangeMethod: PropTypes.func.isRequired,
	handleError: PropTypes.func.isRequired,
	currentPage: PropTypes.string.isRequired,
	setCurrentPage: PropTypes.func.isRequired,
	isChange: PropTypes.bool,
	cooldown: PropTypes.number.isRequired,
	startCooldown: PropTypes.func.isRequired,
	isModal: PropTypes.bool,
	handleOnFinish: PropTypes.func,
};

EmailEnrollment.defaultProps = {
	error: '',
	isChange: false,
	isModal: false,
	handleOnFinish: () => {},
};

export default EmailEnrollment;
